/**
 * The loan panel — one loan's progress, from YNAB's balance and our terms.
 *
 * The thing this screen has to get right is honesty about the back end of the
 * curve. A NZ mortgage is fixed for one to five years and then reprices, so a
 * payoff date in 2055 is a projection at an assumed rate, not a fact. Everywhere
 * that date appears it is labelled as such, and the chart draws the assumed
 * stretch differently from the fixed one.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Money } from '@ynab/core'
import { useState } from 'react'
import { type LoanResponse, queryKeys, setLoanCharge, setLoanTerms } from '../api.ts'
import { fmt, fmtWhole, shortDate } from '../lib/format.ts'
import { Portal } from '../lib/Portal.tsx'

// Derived from the API rather than imported from core: JSON has no branded
// types, so the amounts here are plain numbers and the core type would not fit.
type ChargePeriod = Extract<
  NonNullable<LoanResponse['projection']>,
  { kind: 'amortising' }
>['periods'][number]

import { useEscapeKey, useScrollLock } from '../lib/usePresence.ts'

const FREQUENCIES = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'fortnightly', label: 'Fortnightly' },
  { id: 'monthly', label: 'Monthly' },
] as const

type Frequency = (typeof FREQUENCIES)[number]['id']

export function LoanPanel({ loan: data }: { loan: LoanResponse }) {
  const [editing, setEditing] = useState(false)
  const accountId = data.accountId
  const { terms, projection, balance } = data

  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-card p-[18px]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-[0.16em] text-muted">
            {data.accountName} · still owing
          </span>
          <span className="font-display text-[34px] leading-none">{fmt(balance)}</span>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="whitespace-nowrap rounded-full bg-sand px-3.5 py-2 text-[12px] text-ink transition-colors hover:bg-sand-deep"
        >
          {terms ? 'Edit the loan' : 'Set the loan up'}
        </button>
      </div>

      {!terms && (
        <p className="m-0 text-[13px] leading-[1.55] text-muted">
          {data.ynab.rateBp !== null
            ? `YNAB has this at ${(data.ynab.rateBp / 100).toFixed(2)}%${data.ynab.minimumPayment ? ` with ${fmt(data.ynab.minimumPayment)} a month` : ''}, but it cannot say when the rate is fixed until or when the loan must be repaid by — which is what makes a New Zealand payoff date honest. `
            : ''}
          Add the terms and this becomes a payoff projection, with what an extra $50 a payment would
          actually save you.
        </p>
      )}

      {/*
        When the figures are knowingly short, say so.

        Every total on this panel is a sum over rows marked interest, drawdown or fee, and
        an unmarked charge is left out of all three rather than assumed into one. That is
        the right call — guessing is what produced $856,766 of interest on The Build — but
        it means "interest so far" can be an understatement, and an understatement nobody
        mentions is just a different wrong number. The register is where it gets answered.
      */}
      {data.unclassified.length > 0 && <Unclassified loan={data} />}

      <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-line-faint pt-3">
        <Figure label="Interest so far" value={fmt(data.interestPaidToDate)} tone="alert" />
        <Figure label="Principal repaid" value={fmt(data.principalPaidToDate)} />
        {/* Only when there are any: a permanent "Fees $0.00" never says anything. */}
        {data.feesPaidToDate > 0 && (
          <Figure label="Fees so far" value={fmt(data.feesPaidToDate)} tone="alert" />
        )}
        {data.drawnToDate > 0 && <Figure label="Drawn down" value={fmt(data.drawnToDate)} />}
      </div>

      {terms && projection && (
        <>
          <div className="grid grid-cols-2 gap-3 border-t border-line-faint pt-3 sm:grid-cols-4">
            <Figure label="Rate" value={`${(terms.annualRateBp / 100).toFixed(2)}%`} />
            {projection.kind !== 'interest-only' && (
              <Figure
                label={`Every ${periodWord(terms.frequency)}`}
                value={terms.payment === null ? '—' : fmt(terms.payment)}
              />
            )}
            {/*
              What the debt costs, as opposed to what leaves the account. The two
              are wildly different early in a mortgage and a bank statement shows
              only the second — one round figure, with no hint that most of it is
              rent on the money rather than progress against it.
            */}
            {(projection.kind === 'amortising' || projection.kind === 'interest-only') && (
              <Figure
                label="Interest a month"
                value={`about ${fmtWhole(projection.monthlyInterest)}`}
              />
            )}
            <Figure
              label={
                projection.kind === 'amortising'
                  ? 'Debt free'
                  : projection.kind === 'interest-only'
                    ? 'Converts'
                    : 'Payoff'
              }
              value={
                projection.kind === 'amortising'
                  ? formatMonth(projection.payoffDate)
                  : projection.kind === 'settled'
                    ? 'Paid off'
                    : projection.kind === 'beyond-horizon'
                      ? `${projection.years}+ years`
                      : projection.kind === 'interest-only'
                        ? projection.convertsOn
                          ? formatMonth(projection.convertsOn)
                          : 'Not set'
                        : 'Not on this payment'
              }
              // Interest-only is not an alarm. It is the arrangement.
              tone={
                projection.kind === 'amortising' || projection.kind === 'interest-only'
                  ? 'plain'
                  : 'alert'
              }
            />
          </div>

          {projection.kind === 'interest-only' && (
            <div className="flex flex-col gap-2 rounded-[10px] border border-line bg-paper p-3.5">
              <span className="text-[13px] leading-[1.5] text-ink-dim">
                Interest only, so nothing comes off the {fmt(data.balance)} yet. At{' '}
                {(terms.annualRateBp / 100).toFixed(2)}% that is about{' '}
                {fmtWhole(projection.monthlyInterest)} a month — worth an envelope, since it is a
                real cost that never reduces the debt.
                {projection.convertsOn
                  ? ` Principal starts coming off around ${formatDay(projection.convertsOn)}; add the repayment then and this becomes a payoff date.`
                  : ' Add the repayment once it converts and this becomes a payoff date.'}
              </span>

              <UpcomingCharges periods={projection.periods} />
            </div>
          )}

          {projection.kind === 'never' && (
            <div className="flex flex-col gap-1 rounded-[10px] border border-blush bg-blush-soft p-3.5">
              <span className="text-[13px] leading-[1.5] text-blush-text">
                From {formatDay(projection.from)} the payment stops covering the interest — at{' '}
                {(projection.rateBp / 100).toFixed(2)}% that is {fmt(projection.interest)} a
                payment, so the balance would start climbing.
              </span>
              {projection.shortfall > 0 && (
                <span className="text-[13px] text-blush-text">
                  It needs {fmt(projection.shortfall)} more each time just to stand still.
                </span>
              )}
            </div>
          )}

          {projection.kind === 'beyond-horizon' && (
            <div className="flex flex-col gap-1 rounded-[10px] border border-blush bg-blush-soft p-3.5">
              <span className="text-[13px] leading-[1.5] text-blush-text">
                This payment does clear the loan, but slowly: {projection.years} years out there
                would still be {fmt(projection.balanceAtHorizon)} owing, so the projection stops
                rather than guess further.
              </span>
              <span className="text-[13px] text-blush-text">
                Almost all of each payment is going to interest. A small increase changes this
                enormously — try one above.
              </span>
            </div>
          )}

          {projection.kind === 'amortising' && (
            // Two columns rather than stacked: full-width the curve is 1300px by
            // 120px, an aspect so flat that thirty years of progress looks like a
            // straight line.
            <div className="grid grid-cols-1 items-start gap-[22px] border-t border-line-faint pt-3 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div className="flex min-w-0 flex-col gap-2">
                <BalanceCurve projection={projection} fixedUntil={terms.fixedUntil ?? null} />
                {/* Said before the rate note, because a flat first stretch on the
                    curve otherwise reads as a bug rather than as the deal. */}
                {projection.convertsOn && (
                  <p className="m-0 text-[12px] leading-[1.5] text-sky-deep">
                    Interest only until {formatDay(terms.interestOnlyUntil ?? '')} — the balance
                    holds at {fmt(data.balance)} and you pay just the interest on it. Principal
                    starts coming off on {formatDay(projection.convertsOn)}
                    {terms.payment === null
                      ? '.'
                      : `, at ${fmt(terms.payment)} a ${periodWord(terms.frequency)}.`}
                  </p>
                )}
                {/*
                  Where the repayment steps up, which is the whole point of holding
                  the term: a refix at a higher rate is not a longer loan, it is a
                  bigger fortnightly payment, and that is the number to plan around.
                */}
                {projection.steps.length > 0 && (
                  <p className="m-0 text-[12px] leading-[1.5] text-blush-text">
                    {projection.steps.map((step, index) => (
                      <span key={step.date}>
                        {index === 0 ? 'From ' : ' Then from '}
                        {formatDay(step.date)} at {(step.rateBp / 100).toFixed(2)}% the repayment
                        becomes <span className="text-ink">{fmt(step.payment)}</span> a{' '}
                        {periodWord(terms.frequency)}
                        {step.stated ? ' (your lender’s figure)' : ''}
                        {index === projection.steps.length - 1 ? '' : ','}
                      </span>
                    ))}
                    {' — '}
                    recalculated so the loan still finishes on time, which is what your lender does
                    when a fixed period ends.
                  </p>
                )}

                <UpcomingCharges periods={projection.periods} />
                <p className="m-0 text-[12px] leading-[1.5] text-muted">
                  {terms.fixedUntil
                    ? `Fixed at ${(terms.annualRateBp / 100).toFixed(2)}% until ${formatDay(terms.fixedUntil)}. After that this assumes ${((terms.revertRateBp ?? terms.annualRateBp) / 100).toFixed(2)}% — a guess, not a rate anyone has offered you, so read the far end of the curve as a shape rather than a date.`
                    : 'Floating, so the whole projection assumes today’s rate holds. It will not — but it is the only honest starting point.'}{' '}
                  {fmt(projection.totalInterest)} of interest still to pay at that rate.
                </p>
              </div>

              {data.whatIf.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-muted">
                    Pay a bit more
                  </span>
                  {data.whatIf.map((option) => (
                    <div
                      key={option.extra}
                      className="flex items-baseline justify-between gap-3 rounded-[10px] bg-paper px-3 py-2.5"
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[12.5px] text-ink">
                          +{fmtWhole(option.extra)} each time
                        </span>
                        <span className="text-[11.5px] text-muted">
                          {fmtWhole(option.interestSaved)} less interest
                        </span>
                      </div>
                      <span className="whitespace-nowrap text-[12.5px] text-sky-deep">
                        {formatSpan(option.monthsSaved)} sooner
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {editing && <LoanSheet loan={data} onClose={() => setEditing(false)} accountId={accountId} />}
    </div>
  )
}

/**
 * Debits nobody has said what they were, with a way to say.
 *
 * Every total on this panel is a sum over rows known to be interest, a drawdown or a
 * fee, and an unknown one is left out of all three rather than assumed into one —
 * guessing is what produced $856,766 of interest on The Build. So the figures can be
 * knowingly short, and this is where that is admitted and answered.
 */
function Unclassified({ loan }: { loan: LoanResponse }) {
  const queryClient = useQueryClient()
  const mark = useMutation({
    mutationFn: (input: { id: string; kind: 'interest' | 'drawdown' | 'fee' }) =>
      setLoanCharge(input.id, input.kind),
    onSuccess: (loans) => queryClient.setQueryData(queryKeys.loans, loans),
  })
  const shown = loan.unclassified.slice(0, 8)

  return (
    <div className="flex flex-col gap-2 rounded-[10px] bg-blush-soft/60 px-3 py-2.5">
      <p className="m-0 text-[12.5px] leading-[1.5] text-ink-dim">
        {loan.unclassified.length === 1
          ? 'One charge has not been marked as interest, a drawdown or a fee, so it is not counted below.'
          : `${loan.unclassified.length} charges have not been marked as interest, a drawdown or a fee, so they are not counted below.`}
      </p>
      {shown.map((charge) => (
        <div key={charge.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
          <span className="w-[52px] text-muted">{shortDate(charge.date)}</span>
          <span className="min-w-0 flex-1 truncate">
            {charge.payee ?? 'No payee'}
            {charge.memo ? <span className="text-muted"> · {charge.memo}</span> : null}
          </span>
          <span>{fmt(charge.amount)}</span>
          <span className="flex gap-1">
            {(['interest', 'drawdown', 'fee'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                disabled={mark.isPending}
                onClick={() => mark.mutate({ id: charge.id, kind })}
                className="rounded-full bg-paper px-2.5 py-1 text-[11px] text-ink-dim transition-colors hover:bg-card disabled:opacity-50"
              >
                {kind === 'interest' ? 'Interest' : kind === 'drawdown' ? 'Drawdown' : 'Fee'}
              </button>
            ))}
          </span>
        </div>
      ))}
      {loan.unclassified.length > shown.length && (
        <span className="text-[11.5px] text-muted">
          and {loan.unclassified.length - shown.length} more, older
        </span>
      )}
    </div>
  )
}

/**
 * The next few payments, split into interest and principal.
 *
 * The split is the thing worth seeing and the one place it exists: the payment is
 * a single figure on the statement, the balance moves by a different one, and
 * nothing in between says why. It also moves — a 30-day month is cheaper than a
 * 31-day one, and every payment shifts a little more of itself onto principal.
 */
function UpcomingCharges({ periods }: { periods: ChargePeriod[] }) {
  const next = periods.slice(0, 6)
  if (next.length === 0) return null

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {next.map((period) => (
        <span key={period.date} className="text-[11.5px] text-muted">
          {formatDay(period.date)} ·{' '}
          <span className="text-blush-deep">{fmtWhole(period.interest)}</span> interest
          {period.principal > 0 && <> · {fmtWhole(period.principal)} off</>}
        </span>
      ))}
    </div>
  )
}

function Figure({
  label,
  value,
  tone = 'plain',
}: {
  label: string
  value: string
  tone?: 'plain' | 'alert'
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-[0.14em] text-muted">{label}</span>
      <span className={`text-[16px] ${tone === 'alert' ? 'text-blush-deep' : 'text-ink'}`}>
        {value}
      </span>
    </div>
  )
}

/**
 * The balance falling away to nothing.
 *
 * Two segments, not one: solid while the rate is fixed and known, dashed once the
 * projection is running on an assumed rate. A single unbroken line would claim a
 * confidence the back half of a 30-year schedule does not have.
 */
function BalanceCurve({
  projection,
  fixedUntil,
}: {
  projection: Extract<LoanResponse['projection'], { kind: 'amortising' }>
  fixedUntil: string | null
}) {
  // One point a year is plenty for a 30-year curve and keeps the path small.
  const step = Math.max(1, Math.floor(projection.periods.length / 60))
  const points = projection.periods.filter(
    (_, i) => i % step === 0 || i === projection.periods.length - 1,
  )
  const peak = Math.max(...points.map((p) => p.balance), 1)

  const x = (i: number) => (i / Math.max(1, points.length - 1)) * 100
  const y = (balance: number) => 100 - (balance / peak) * 100

  const fixedCount = fixedUntil ? points.filter((p) => p.date <= fixedUntil).length : points.length
  const line = (from: number, to: number) =>
    points
      .slice(from, to)
      .map((p, i) => `${x(from + i)},${y(p.balance)}`)
      .join(' ')

  return (
    <div className="flex flex-col gap-1">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="h-[160px] w-full"
        role="img"
        aria-label={`Balance falling to zero by ${formatMonth(projection.payoffDate)}`}
      >
        <title>Projected balance</title>
        <polygon
          points={`0,100 ${line(0, points.length)} 100,100`}
          fill="var(--color-sky-soft)"
          stroke="none"
        />
        <polyline
          points={line(0, fixedCount + 1)}
          fill="none"
          stroke="var(--color-sky-deep)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        {fixedCount < points.length && (
          <polyline
            points={line(Math.max(0, fixedCount), points.length)}
            fill="none"
            stroke="var(--color-sky-deep)"
            strokeWidth="2"
            strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      <div className="flex justify-between text-[11px] text-muted">
        <span>{formatMonth(points[0]?.date ?? '')}</span>
        {fixedUntil && <span>fixed to {formatMonth(fixedUntil)} · assumed after</span>}
        <span>{formatMonth(projection.payoffDate)}</span>
      </div>
    </div>
  )
}

/** Setting the terms. A sheet rather than inline: it is six fields, entered once a year. */
function LoanSheet({
  loan,
  accountId,
  onClose,
}: {
  loan: LoanResponse
  accountId: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  useScrollLock(true)
  useEscapeKey(true, onClose)

  const existing = loan.terms
  // With no terms saved yet, start from what YNAB already holds rather than a blank box.
  const [rate, setRate] = useState(
    existing
      ? (existing.annualRateBp / 100).toFixed(2)
      : loan.ynab.rateBp !== null
        ? (loan.ynab.rateBp / 100).toFixed(2)
        : '',
  )
  const [interestOnly, setInterestOnly] = useState(
    existing ? existing.interestOnlyOpenEnded === true || existing.payment === null : false,
  )
  const [payment, setPayment] = useState(
    existing?.payment != null
      ? String(existing.payment / 1000)
      : !existing && loan.ynab.minimumPayment
        ? String(loan.ynab.minimumPayment / 1000)
        : '',
  )
  // YNAB's minimum payment is monthly, so a form prefilled from it has to say monthly.
  const [frequency, setFrequency] = useState<Frequency>(
    existing?.frequency ?? (loan.ynab.minimumPayment ? 'monthly' : 'fortnightly'),
  )
  const [nextDate, setNextDate] = useState(existing?.nextPaymentDate ?? '')
  const [fixedUntil, setFixedUntil] = useState(existing?.fixedUntil ?? '')
  const [interestOnlyUntil, setInterestOnlyUntil] = useState(existing?.interestOnlyUntil ?? '')
  const [maturity, setMaturity] = useState(existing?.maturityDate ?? '')
  /*
   * A key of its own per row, not the array index. These are edited and removed
   * in place, and an index key makes React reuse the wrong input when one goes —
   * you delete the first period and the second one's typing appears in its box.
   */
  const [fixes, setFixes] = useState<
    { key: string; rate: string; until: string; payment: string }[]
  >(
    (existing?.laterFixes ?? []).map((fix) => ({
      key: crypto.randomUUID(),
      rate: (fix.annualRateBp / 100).toFixed(2),
      until: fix.until ?? '',
      payment: fix.payment == null ? '' : String(fix.payment / 1000),
    })),
  )
  const [revertRate, setRevertRate] = useState(
    existing?.revertRateBp ? (existing.revertRateBp / 100).toFixed(2) : '',
  )

  const rateBp = Math.round(Number.parseFloat(rate) * 100)
  const paymentMu = Money.parse(payment)
  // Interest-only needs no repayment — that is the whole point of the toggle.
  const valid =
    Number.isFinite(rateBp) &&
    rateBp >= 0 &&
    nextDate !== '' &&
    (interestOnly || (paymentMu !== null && paymentMu > 0))

  const save = useMutation({
    mutationFn: () =>
      setLoanTerms(accountId, {
        annualRateBp: rateBp,
        // Null rather than nought: a loan with no agreed repayment has none, and
        // storing 0 would read as a payment that covers nothing.
        paymentAmount: interestOnly ? null : (paymentMu ?? 0),
        // Interest is billed monthly, so the frequency question does not arise.
        frequency: interestOnly ? 'monthly' : frequency,
        nextPaymentDate: nextDate,
        fixedUntil: fixedUntil || null,
        revertRateBp: revertRate ? Math.round(Number.parseFloat(revertRate) * 100) : null,
        interestOnlyUntil: interestOnly ? interestOnlyUntil || null : null,
        interestOnlyOpenEnded: interestOnly && interestOnlyUntil === '',
        maturityDate: maturity || null,
        laterFixes: fixes
          .filter((fix) => Number.isFinite(Number.parseFloat(fix.rate)))
          .map((fix) => ({
            annualRateBp: Math.round(Number.parseFloat(fix.rate) * 100),
            until: fix.until || null,
            payment: Money.parse(fix.payment),
          })),
      }),
    onSuccess: (loans) => {
      queryClient.setQueryData(queryKeys.loans, loans)
      onClose()
    },
  })

  return (
    <Portal>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          data-visible="true"
          className="kete-backdrop absolute inset-0 cursor-default bg-ink/30"
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${loan.accountName} loan terms`}
          data-visible="true"
          className="kete-sheet relative flex max-h-[calc(100vh-48px)] w-[392px] max-w-full flex-col gap-4 overflow-y-auto rounded-[20px] bg-paper p-6 shadow-[var(--shadow-pop)]"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-[3px]">
              <span className="text-[10px] uppercase tracking-[0.16em] text-muted">The loan</span>
              <span className="font-display text-[27px] leading-[1.1]">{loan.accountName}</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="h-[30px] w-[30px] flex-none rounded-full border border-line bg-card text-[14px] text-ink transition-colors hover:bg-sand"
            >
              ✕
            </button>
          </div>

          <Field label="Interest rate">
            <div className="flex items-center gap-2">
              <input
                value={rate}
                onChange={(event) => setRate(event.target.value)}
                inputMode="decimal"
                placeholder="4.85"
                className="w-full rounded-[10px] border border-line bg-card px-3 py-2.5 text-[15px] text-ink outline-none focus:border-ink"
              />
              <span className="text-[14px] text-muted">%</span>
            </div>
          </Field>

          {/*
            The toggle governs the two fields below it, so it sits above them
            rather than in a section of its own further down. Turning it on takes
            the repayment and the frequency away entirely — asking for a repayment
            nobody has agreed was the thing that made a build loan unrecordable.
          */}
          <label className="flex cursor-pointer items-start gap-2.5 rounded-[12px] border border-line bg-card px-3 py-3">
            <input
              type="checkbox"
              checked={interestOnly}
              onChange={(event) => setInterestOnly(event.target.checked)}
              className="mt-[3px] accent-[var(--color-cta)]"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-[13.5px] text-ink">Interest only at the moment</span>
              <span className="text-[12px] leading-[1.5] text-muted">
                A construction loan, or any period where you are only servicing the interest. The
                balance holds where it is and there is no repayment to give.
              </span>
            </span>
          </label>

          {!interestOnly && (
            <>
              <Field label="Repayment">
                <input
                  value={payment}
                  onChange={(event) => setPayment(event.target.value)}
                  inputMode="decimal"
                  placeholder="970.00"
                  className="rounded-[10px] border border-line bg-card px-3 py-2.5 text-[15px] text-ink outline-none focus:border-ink"
                />
              </Field>

              <Field label="How often">
                <div className="grid grid-cols-3 gap-[7px]">
                  {FREQUENCIES.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setFrequency(option.id)}
                      className={`rounded-[10px] border px-2 py-2.5 text-[12.5px] text-ink transition-colors ${
                        frequency === option.id
                          ? 'border-ink bg-card'
                          : 'border-line bg-transparent hover:border-line-strong'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </Field>
            </>
          )}

          <Field label={interestOnly ? 'Interest charged next on' : 'Next payment'}>
            <input
              type="date"
              value={nextDate}
              onChange={(event) => setNextDate(event.target.value)}
              className="rounded-[10px] border border-line bg-card px-3 py-2.5 text-[14px] text-ink outline-none focus:border-ink"
            />
          </Field>

          <div className="flex flex-col gap-3 rounded-[14px] bg-sand p-4">
            <span className="text-[10px] uppercase tracking-[0.16em] text-ink-dim">
              The fixed term
            </span>
            <p className="m-0 text-[12.5px] leading-[1.5] text-ink-dim">
              Leave these blank if you are floating. Filling them in is what stops the payoff date
              pretending today’s rate lasts thirty years.
            </p>
            <Field label="Fixed until">
              <input
                type="date"
                value={fixedUntil}
                onChange={(event) => setFixedUntil(event.target.value)}
                className="rounded-[10px] border border-line bg-paper px-3 py-2.5 text-[14px] text-ink outline-none focus:border-ink"
              />
            </Field>
            {/*
              The field that makes a refix behave like a bank. A New Zealand lender
              holds the term and moves the repayment, so without a date to work back
              to, the projection can only freeze the repayment and let the term run
              away — which is how $969.87 came out as "40+ years".
            */}
            <Field label="Must be repaid by">
              <input
                type="date"
                value={maturity}
                onChange={(event) => setMaturity(event.target.value)}
                className="rounded-[10px] border border-line bg-paper px-3 py-2.5 text-[14px] text-ink outline-none focus:border-ink"
              />
            </Field>

            {/* A rate you have already signed for is not a guess and should not be
                drawn as one. */}
            <div className="flex flex-col gap-2">
              <span className="text-[11px] text-ink-dim">Then already fixed at</span>
              <p className="m-0 text-[11.5px] leading-[1.5] text-muted">
                Leave the repayment blank and it is worked out from the date above. Fill it in and
                your lender's figure is used instead — they round, and some hold it a little above
                what the term needs.
              </p>
              {fixes.map((fix, index) => (
                <div key={fix.key} className="flex flex-wrap items-center gap-1.5">
                  <input
                    value={fix.rate}
                    onChange={(event) =>
                      setFixes((all) =>
                        all.map((f) =>
                          f.key === fix.key ? { ...f, rate: event.target.value } : f,
                        ),
                      )
                    }
                    inputMode="decimal"
                    placeholder="5.79"
                    aria-label={`Rate for fixed period ${index + 2}`}
                    className="w-[68px] rounded-[8px] border border-line bg-paper px-2 py-1.5 text-[13px] text-ink outline-none focus:border-ink"
                  />
                  <span className="text-[13px] text-muted">% until</span>
                  <input
                    type="date"
                    value={fix.until}
                    onChange={(event) =>
                      setFixes((all) =>
                        all.map((f) =>
                          f.key === fix.key ? { ...f, until: event.target.value } : f,
                        ),
                      )
                    }
                    aria-label={`End of fixed period ${index + 2}`}
                    className="min-w-0 flex-1 rounded-[8px] border border-line bg-paper px-2 py-1.5 text-[13px] text-ink outline-none focus:border-ink"
                  />
                  <button
                    type="button"
                    onClick={() => setFixes((all) => all.filter((f) => f.key !== fix.key))}
                    aria-label="Remove this fixed period"
                    className="px-1 text-[13px] text-muted transition-colors hover:text-blush-deep"
                  >
                    ×
                  </button>
                  <span className="flex w-full items-center gap-1.5 pl-1">
                    <span className="text-[12px] text-muted">paying</span>
                    <input
                      value={fix.payment}
                      onChange={(event) =>
                        setFixes((all) =>
                          all.map((f) =>
                            f.key === fix.key ? { ...f, payment: event.target.value } : f,
                          ),
                        )
                      }
                      inputMode="decimal"
                      placeholder="worked out"
                      aria-label={`Repayment for fixed period ${index + 2}`}
                      className="w-[104px] rounded-[8px] border border-line bg-paper px-2 py-1.5 text-[13px] text-ink outline-none focus:border-ink"
                    />
                    <span className="text-[12px] text-muted">
                      a {interestOnly ? 'month' : periodWord(frequency)}
                    </span>
                  </span>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setFixes((all) => [
                    ...all,
                    { key: crypto.randomUUID(), rate: '', until: '', payment: '' },
                  ])
                }
                className="self-start rounded-full bg-paper px-2.5 py-1 text-[11px] text-ink-dim transition-colors hover:bg-card"
              >
                + Another fixed period
              </button>
            </div>

            <Field label="Assume it reverts to">
              <div className="flex items-center gap-2">
                <input
                  value={revertRate}
                  onChange={(event) => setRevertRate(event.target.value)}
                  inputMode="decimal"
                  placeholder={rate || '6.50'}
                  className="w-full rounded-[10px] border border-line bg-paper px-3 py-2.5 text-[15px] text-ink outline-none focus:border-ink"
                />
                <span className="text-[14px] text-muted">%</span>
              </div>
            </Field>
          </div>

          {interestOnly && (
            <div className="flex flex-col gap-3 rounded-[14px] bg-sand p-4">
              <span className="text-[10px] uppercase tracking-[0.16em] text-ink-dim">
                When it converts
              </span>
              <p className="m-0 text-[12.5px] leading-[1.5] text-ink-dim">
                Leave this blank if you do not know yet — a build converts when it is finished, and
                that is not a date anyone has on day one. Fill it in and the projection can show
                where principal starts coming off.
              </p>
              <Field label="Interest only until">
                <input
                  type="date"
                  value={interestOnlyUntil}
                  onChange={(event) => setInterestOnlyUntil(event.target.value)}
                  className="rounded-[10px] border border-line bg-paper px-3 py-2.5 text-[14px] text-ink outline-none focus:border-ink"
                />
              </Field>
            </div>
          )}

          {save.isError && (
            <p className="m-0 rounded-[10px] bg-blush-soft px-3 py-2.5 text-[12.5px] text-blush-deep">
              {save.error instanceof Error ? save.error.message : 'Could not save those terms.'}
            </p>
          )}

          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
            className="rounded-full bg-cta py-3.5 text-[14px] font-medium text-cta-fg transition-colors hover:bg-cta-hover disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save the loan'}
          </button>
        </div>
      </div>
    </Portal>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is nested via children, which associates implicitly — the rule cannot see through the prop
    <label className="flex flex-col gap-[7px]">
      <span className="text-[11px] uppercase tracking-[0.14em] text-muted">{label}</span>
      {children}
    </label>
  )
}

/** "Dec 2055" — a payoff date is a month, not a day. Pretending otherwise oversells it. */
function formatMonth(isoDate: string): string {
  if (!isoDate) return ''
  return new Intl.DateTimeFormat('en-NZ', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${isoDate}T00:00:00Z`))
}

function periodWord(frequency: 'weekly' | 'fortnightly' | 'monthly'): string {
  if (frequency === 'monthly') return 'month'
  return frequency === 'weekly' ? 'week' : 'fortnight'
}

function formatDay(isoDate: string): string {
  return new Intl.DateTimeFormat('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${isoDate}T00:00:00Z`))
}

/** "3 years 2 months" reads better than "38 months" at this scale. */
function formatSpan(months: number): string {
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'}`
  const years = Math.floor(months / 12)
  const rest = months % 12
  const yearPart = `${years} ${years === 1 ? 'year' : 'years'}`
  return rest === 0 ? yearPart : `${yearPart} ${rest} ${rest === 1 ? 'month' : 'months'}`
}
